import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, BookmarkPlus, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { rhm, apiError } from "../api";
import { Badge, Btn, Card, Empty, Select } from "../ui";
import { SECTION_TYPES, blankSection } from "../../site/registry";
import { Inspector } from "./websiteFields";
import { AddSection, SaveTemplateDialog, InlineEditor } from "./websiteDialogs";
import { BundlesManager } from "./websiteBundles";
import { ProductLivePreview } from "../components/ProductLivePreview";
import { StyleDialog } from "../components/productEditBridge";

const EXTRA_TYPES = ["plain_cards", "product_pairing", "rich_text", "image_banner", "faq",
    "testimonials", "blog_highlight", "consultation_cta", "benefits", "marquee"];

const BlockRow = ({ block, label, first, last, editable, onMove, onToggle, ns = "tmpl" }) => (
    <div className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3"
        data-testid={`pl-${ns}-block-${block.key}`}>
        <div>
            <div className="text-sm">{label}</div>
            <div className={`mono text-[9px] tracking-[0.16em] mt-0.5 ${block.visible ? "text-lime" : "text-zinc-600"}`}>
                {block.visible ? "SHOWN" : "HIDDEN"}
            </div>
        </div>
        <div className="flex items-center gap-1.5">
            <Btn variant="ghost" disabled={!editable || first} onClick={() => onMove(-1)}
                data-testid={`pl-${ns}-up-${block.key}`}><ArrowUp className="w-3.5 h-3.5" /></Btn>
            <Btn variant="ghost" disabled={!editable || last} onClick={() => onMove(1)}
                data-testid={`pl-${ns}-down-${block.key}`}><ArrowDown className="w-3.5 h-3.5" /></Btn>
            <Btn variant="ghost" disabled={!editable} onClick={onToggle} data-testid={`pl-${ns}-toggle-${block.key}`}>
                {block.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </Btn>
        </div>
    </div>
);

export const ProductPagesManager = ({ editable }) => {
    const qc = useQueryClient();
    const [productId, setProductId] = useState("");
    const [selected, setSelected] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [presetFor, setPresetFor] = useState(null);
    const [inline, setInline] = useState(null);
    const [styleFor, setStyleFor] = useState(null);

    const { data: catalogue } = useQuery({
        queryKey: ["pl-blocks"], queryFn: async () => (await rhm.get("/site/product-blocks")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"], queryFn: async () => (await rhm.get("/products")).data,
    });
    const { data: template } = useQuery({
        queryKey: ["pl-template"], queryFn: async () => (await rhm.get("/site/product-template")).data,
    });
    const { data: page } = useQuery({
        queryKey: ["pl-page", productId],
        queryFn: async () => (await rhm.get(`/site/product-pages/${productId}`)).data,
        enabled: !!productId,
    });

    const saveTemplate = useMutation({
        mutationFn: async (body) => (await rhm.put("/site/product-template", body)).data,
        onSuccess: (data) => {
            qc.setQueryData(["pl-template"], data);
            qc.invalidateQueries({ queryKey: ["pl-template"] });
            toast.success("Template saved");
        },
        onError: (e) => toast.error(apiError(e)),
    });
    const savePage = useMutation({
        mutationFn: async (body) => (await rhm.put(`/site/product-pages/${productId}`, body)).data,
        onSuccess: (data) => {
            qc.setQueryData(["pl-page", productId], (prev) => ({ ...(prev || {}), ...data }));
            qc.invalidateQueries({ queryKey: ["pl-page", productId] });
            toast.success("Product page saved");
        },
        onError: (e) => toast.error(apiError(e)),
    });

    const label = (key) => (catalogue?.blocks || []).find((b) => b.key === key)?.label || key;
    const blocks = template?.blocks || [];
    const extras = page?.extras || [];
    const ownBlocks = page?.blocks || [];
    const inherits = page?.inherit_blocks !== false;

    const moveOwn = (index, dir) => {
        const next = [...(inherits ? blocks : ownBlocks)];
        const [row] = next.splice(index, 1);
        next.splice(index + dir, 0, row);
        savePage.mutate({ blocks: next, inherit_blocks: false });
    };
    const toggleOwn = (index) => {
        const source = inherits ? blocks : ownBlocks;
        savePage.mutate({
            blocks: source.map((b, i) => (i === index ? { ...b, visible: !b.visible } : b)),
            inherit_blocks: false,
        });
    };

    const moveBlock = (index, dir) => {
        const next = [...blocks];
        const [row] = next.splice(index, 1);
        next.splice(index + dir, 0, row);
        saveTemplate.mutate({ blocks: next });
    };
    const toggleBlock = (index) => {
        const next = blocks.map((b, i) => (i === index ? { ...b, visible: !b.visible } : b));
        saveTemplate.mutate({ blocks: next });
    };

    const addExtra = (type) => {
        const section = { ...blankSection(type), id: `psec_${Date.now()}`, visible: true };
        savePage.mutate({ extras: [...extras, section] });
        setSelected(section.id);
        setShowAdd(false);
    };
    const addPreset = (preset) => {
        const section = { id: `psec_${Date.now()}`, type: preset.section_type, name: preset.name,
            visible: true, props: JSON.parse(JSON.stringify(preset.props || {})) };
        savePage.mutate({ extras: [...extras, section] });
        setSelected(section.id);
        setShowAdd(false);
        toast.success(`"${preset.name}" added — edit the copy here without changing the preset`);
    };
    const patchExtra = (id, patch) =>
        savePage.mutate({ extras: extras.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
    const removeExtra = (id) => {
        savePage.mutate({ extras: extras.filter((s) => s.id !== id) });
        setSelected(null);
    };

    const current = extras.find((s) => s.id === selected);
    const product = products.find((p) => p.id === productId);
    const styled = extras.find((s) => s.id === styleFor);
    const previewLayout = { blocks: (inherits ? blocks : ownBlocks).filter((b) => b.visible), extras };

    const moveExtra = (id, dir) => {
        const list = [...extras];
        const i = list.findIndex((s) => s.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        savePage.mutate({ extras: list });
    };

    // Clicks coming back from the live preview window.
    const onPreviewEdit = (payload) => {
        if (!editable) return toast.error("You do not have permission to edit the website");
        const section = extras.find((s) => s.id === payload.id);
        if (payload.kind === "product-text" || payload.kind === "product-images") {
            return toast.message(`Wording and photos live in Products → ${product?.name || "this product"}`);
        }
        if (payload.kind === "add-section") return setShowAdd(true);
        if (payload.kind === "section-text") return setInline(payload);
        if (payload.kind !== "section-action" || !section) return undefined;
        if (payload.action === "up") return moveExtra(payload.id, -1);
        if (payload.action === "down") return moveExtra(payload.id, 1);
        if (payload.action === "hide") return patchExtra(payload.id, { visible: section.visible === false });
        if (payload.action === "delete") {
            return window.confirm("Delete this section from the product page?") && removeExtra(payload.id);
        }
        if (payload.action === "duplicate") {
            const copy = JSON.parse(JSON.stringify(section));
            copy.id = `psec_${Date.now()}`;
            copy.name = `${copy.name || copy.type} copy`;
            const list = [...extras];
            list.splice(extras.indexOf(section) + 1, 0, copy);
            return savePage.mutate({ extras: list });
        }
        if (payload.action === "style") return setStyleFor(payload.id);
        if (payload.action === "preset") return setPresetFor(section);
        return setSelected(payload.id);
    };

    return (
        <div className="lg:col-span-2 grid lg:grid-cols-2 gap-5" data-testid="product-pages-manager">
            <Card title="Every product page" sub="Order and visibility apply to all products — new ones inherit it automatically.">
                <div className="space-y-2">
                    {blocks.map((b, i) => (
                        <BlockRow key={b.key} block={b} label={label(b.key)} editable={editable} ns="tmpl"
                            first={i === 0} last={i === blocks.length - 1}
                            onMove={(dir) => moveBlock(i, dir)} onToggle={() => toggleBlock(i)} />
                    ))}
                </div>
                <p className="mt-4 text-xs text-zinc-500">
                    The image gallery, price and buy box stay at the top — everything below them is yours to arrange.
                    “Custom sections” is where each product’s own sections appear.
                </p>
            </Card>

            <Card title="This product’s own sections"
                action={<Select value={productId} onChange={(e) => { setProductId(e.target.value); setSelected(null); }}
                    data-testid="pl-product-select" className="!w-56"
                    options={[{ value: "", label: "Pick a product…" },
                        ...products.map((p) => ({ value: p.id, label: `${p.product_code} · ${p.name}` }))]} />}>
                {!productId ? <Empty msg="Pick a product to add sections to its page." /> : (
                    <>
                        <div className="border border-white/10 p-4 mb-5">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-sm">Block order for this product</div>
                                    <div className={`mono text-[9px] tracking-[0.16em] mt-0.5 ${inherits ? "text-zinc-500" : "text-gold"}`}
                                        data-testid="pl-inherit-state">
                                        {inherits ? "FOLLOWING THE SHARED TEMPLATE" : "CUSTOM FOR THIS PRODUCT"}
                                    </div>
                                </div>
                                {!inherits && (
                                    <Btn variant="ghost" disabled={!editable} data-testid="pl-reset-inherit"
                                        onClick={() => savePage.mutate({ inherit_blocks: true })}>
                                        BACK TO TEMPLATE
                                    </Btn>
                                )}
                            </div>
                            <div className="mt-3 space-y-2">
                                {(inherits ? blocks : ownBlocks).map((b, i, arr) => (
                                    <BlockRow key={b.key} block={b} label={label(b.key)} editable={editable} ns="own"
                                        first={i === 0} last={i === arr.length - 1}
                                        onMove={(dir) => moveOwn(i, dir)} onToggle={() => toggleOwn(i)} />
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            {EXTRA_TYPES.filter((t) => SECTION_TYPES.some((s) => s.type === t)).map((t) => (
                                <Btn key={t} variant="ghost" disabled={!editable} onClick={() => addExtra(t)}
                                    data-testid={`pl-add-${t}`}>
                                    <Plus className="w-3 h-3 inline mr-1" />
                                    {SECTION_TYPES.find((s) => s.type === t).label.toUpperCase()}
                                </Btn>
                            ))}
                            <Btn disabled={!editable} onClick={() => setShowAdd(true)} data-testid="pl-open-presets">
                                <BookmarkPlus className="w-3 h-3 inline mr-1" /> PRESETS
                            </Btn>
                        </div>

                        <div className="mt-5 space-y-2">
                            {!extras.length && <Empty msg="No extra sections yet on this product." />}
                            {extras.map((s) => (
                                <div key={s.id} className={`border px-4 py-3 flex items-center justify-between gap-3 ${
                                    selected === s.id ? "border-gold" : "border-white/10"}`}
                                    data-testid={`pl-extra-${s.type}`}>
                                    <button type="button" className="text-left" onClick={() => setSelected(s.id)}>
                                        <div className="text-sm">{SECTION_TYPES.find((x) => x.type === s.type)?.label || s.type}</div>
                                        <div className="mono text-[9px] tracking-[0.16em] text-zinc-500 mt-0.5">
                                            {s.props?.heading || s.props?.eyebrow || "—"}
                                        </div>
                                    </button>
                                    <div className="flex items-center gap-1.5">
                                        <Badge tone={s.visible === false ? "muted" : "success"}>
                                            {s.visible === false ? "Hidden" : "Live"}
                                        </Badge>
                                        <Btn variant="ghost" disabled={!editable}
                                            onClick={() => patchExtra(s.id, { visible: s.visible === false })}>
                                            {s.visible === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                        </Btn>
                                        <Btn variant="ghost" disabled={!editable} data-testid={`pl-preset-${s.type}`}
                                            title="Save as preset" onClick={() => setPresetFor(s)}>
                                            <BookmarkPlus className="w-3.5 h-3.5" />
                                        </Btn>
                                        <Btn variant="ghost" disabled={!editable} onClick={() => removeExtra(s.id)}
                                            data-testid={`pl-remove-${s.type}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {current && (
                            <div className="mt-6 border-t border-white/10 pt-5" data-testid="pl-inspector">
                                <Inspector section={current} products={products} editable={editable}
                                    setProp={(key, value) => patchExtra(current.id, {
                                        props: { ...(current.props || {}), [key]: value } })}
                                    setSection={(patch) => patchExtra(current.id, patch)} />
                            </div>
                        )}
                    </>
                )}
            </Card>

            <div className="lg:col-span-2">
                {productId ? (
                    <Card title="Live preview of this product page"
                        sub="Exactly what a visitor sees. Click any section's tools to colour, move, hide or delete it.">
                        <ProductLivePreview product={product} layout={previewLayout} onEdit={onPreviewEdit} height="80vh" />
                    </Card>
                ) : (
                    <Card title="Live preview of this product page">
                        <Empty msg="Pick a product above to see its page here." />
                    </Card>
                )}
            </div>

            <div className="lg:col-span-2">
                <BundlesManager editable={editable} />
            </div>

            {inline && (
                <InlineEditor payload={inline} onClose={() => setInline(null)}
                    onSave={(value) => {
                        const section = extras.find((s) => s.id === inline.id);
                        patchExtra(inline.id, { props: { ...(section.props || {}), [inline.field]: value } });
                        setInline(null);
                    }} />
            )}
            {styled && (
                <StyleDialog section={styled} onClose={() => setStyleFor(null)}
                    onChange={(patch) => patchExtra(styled.id, { props: { ...(styled.props || {}), ...patch } })} />
            )}

            {showAdd && <AddSection types={EXTRA_TYPES} onClose={() => setShowAdd(false)}
                onPick={addExtra} onPickTemplate={addPreset} />}
            {presetFor && <SaveTemplateDialog section={presetFor} onClose={() => setPresetFor(null)} />}
        </div>
    );
};

export default ProductPagesManager;
